/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'jquery',
	'doT',
	'./StaticWidget',
	'text!./NewWidget.html'
], function ($, dot, StaticWidget, NewWidget) {

	const NewWidgetTemplate = dot.template(NewWidget);

	/**
	 * The UrlWidget is a generic class for widgets which load some markup based on a URL resource
	 */
	const F12 = 123;
	const ESCAPE = 27;
	const MIN_HEIGHT_SHOW_BG_ICON = 280;
	const MIN_WIDTH_SHOW_BG_ICON = 180;
	const MIN_WIDTH_SHOW_LABELS = 250;
	const MIN_HEIGHT_SHOW_LABELS = 180;


	let UrlWidget = null;
	UrlWidget = StaticWidget.extend({
		init: function () {
			UrlWidget.inherited('init', this, arguments);
		},

		initialize: function () {
			return Promise.resolve();
		},

		getId: function () {
			return this.id;
		},

		onContainerReady: function () {
			UrlWidget.inherited('onContainerReady', this, arguments);
			if (this.isInlineEditMode()) {
				this.renderInlineEditUi();
			}
			this.updateModeMarkup();
		},

		/**
		 * Virtual method
		 * @return boolean true iff inline edit mode should be shown (i.e. not the actual widget payload rendering)
		 */
		isInlineEditMode: function () {
			return false;
		},

		onConsumeMode: function () {
			UrlWidget.inherited('onConsumeMode', this, arguments);
			var $container = this.$el.find('.staticContent');
			$container.removeClass('authoring');
			$container.addClass('consume');
		},

		onAuthoringMode: function () {
			UrlWidget.inherited('onAuthoringMode', this, arguments);
			var $container = this.$el.find('.staticContent');
			$container.removeClass('consume');
			$container.addClass('authoring');
		},

		/**
		 * To be overridden by child module
		 * @return {string} the label for empty state
		 */
		getEmptyStateLabel: function(){
			return null;
		},

		/**
		 * To be overridden by child module
		 * @return {string}the URL hint label for empty state
		 */
		getEmptyStateUrlLabel: function(){
			return null;
		},

		/**
		 * To be overridden by child module
		 * @return {string} the helper text label for empty state
		 */
		getEmptyStateHelperLabel: function(){
			return null;
		},

		/**
		 * Virtual method
		 * @return The caption for the inline edit textbox
		 */
		getInlineEditCaption: function () {
			return null;
		},

		/**
		 * Virtual method
		 * @return The text to show in consume mode when no URL has been specified
		 */
		getMissingUrlText: function () {
			return null;
		},

		/**
		 * Virtual method
		 * @return The text for aria label
		 */
		getAriaLabelText: function () {
			return null;
		},

		/**
		 * Virtual method
		 * @return The content class, used to hook into default images for pinning
		 */
		getContentClass: function () {
			return null;
		},

		/**
		 * @override widgetBase.resize()
		 * Hide the empty state image when the widget content's hight < 280px or width < 180px
		 * Hide the empty state labels when the widget content's hight < 250px or width < 180px
		 */
		resize: function () {
			this._hideElements();
		},

		_isHidingLabels: function() {
			if (this.$el && this.$el.length) {
				const height = this.$el.height();
				const width = this.$el.width();
				return height < MIN_HEIGHT_SHOW_LABELS || width < MIN_WIDTH_SHOW_LABELS;
			}
			return true;
		},

		_isHidingBGImg: function() {
			if (this.$el && this.$el.length) {
				const height = this.$el.height();
				const width = this.$el.width();
				return height < MIN_HEIGHT_SHOW_BG_ICON || width < MIN_WIDTH_SHOW_BG_ICON;
			}
			return true;
		},

		_hideElements: function() {
			if (this.$el && this.$el.length) {
				const $image = this.$el.find('.emptyStatePlaceholder__image');
				const $label = this.$el.find('.emptyStatePlaceholder__label');
				const $urlLabel = this.$el.find('.emptyStatePlaceholder__urlLabel');
				const $helperLabel = this.$el.find('.emptyStatePlaceholder__helperLabel');
				const $placeHolder = this.$el.find('.emptyStatePlaceholder');
				const $errorMsg = this.$el.find('.emptyStatePlaceholder .errorMessage');
				const isHidingLabels = this._isHidingLabels();
				const shouldShowBackgroundImage = this._isHidingBGImg();
				$image.toggleClass('hide', shouldShowBackgroundImage);
				$placeHolder.toggleClass('shrink', isHidingLabels);//Recenter inline input element
				$label.toggleClass('hide', isHidingLabels);
				$urlLabel.toggleClass('hide', isHidingLabels);
				$helperLabel.toggleClass('hide', isHidingLabels);
				$errorMsg.toggleClass('hide', isHidingLabels);
			}
		},

		renderInlineEditUi: function () {
			if (!this.$el.find('.inlineEditInput').length) {
				const $parentContainer = this._getParentContainer();
				if($parentContainer && !$parentContainer.hasClass('urlWidgetInlineEdit')){
					$parentContainer.addClass('urlWidgetInlineEdit');
				}
				this.$el.find('.staticContent').replaceWith(NewWidgetTemplate({
					emptyStateLabel: this.getEmptyStateLabel(),
					emptyStateUrlLabel: this.getEmptyStateUrlLabel(),
					title: this.getInlineEditCaption(),
					helperText: this.getEmptyStateHelperLabel(),
					missingUrlText: this.getMissingUrlText(),
					mediaAriaLabel: this.getAriaLabelText(),
					contentClass: this.getContentClass(),
					includeAuthoringMode: true,
					isHidingBGImg: this._isHidingBGImg(),
					isHidingLabels: this._isHidingLabels()
				}));

				var $input = this.$el.find('.staticContent input');

				$input.on('click touchend', function () {
					// give focus to the text input
					$input.focus();
				}.bind(this));

				$(this.el).on('keydown', '.inlineEditInput', this._inlineKeyDown.bind(this));
				$(this.el).on('keyup', '.inlineEditInput', this._inlineKeyUp.bind(this));
				$(this.el).on('input', '.inlineEditInput', this._inlineInput.bind(this));

				$(this.el).on('click touchend', this._onClick.bind(this));

				//Might be called multiple time so we need to cleanup
				$(this.el).off('blur.urlWidget');
				// re-register
				$(this.el).on('blur.urlWidget', '.inlineEditInput', this._onBlur.bind(this));
			}
		},

		_onClick(event) {
			const editInput = this.el.querySelector('.inlineEditInput');
			if (editInput && event.target !== editInput) {
				editInput.blur();
			}
		},

		_getParentContainer(){
			if(this.$el && this.$el.length){
				const contentWrapper = this.$el[0];
				if(contentWrapper){
					const parentContainer = contentWrapper.parentElement;
					if(parentContainer){
						const $parentContainer = $(parentContainer);
						if($parentContainer.hasClass('widget')){
							return $parentContainer;
						}
					}
				}
			}
			return null;
		},

		_onBlur: function () {
			if (this.$el.find('.inlineEditInput').val()) {
				this._uiSetUrl();
			}

		},

		getLabel: function () {
			// To be implemented by concrete class
		},

		_inlineKeyDown: function (evt) {
			if (evt.keyCode === 13) {
				this._uiSetUrl();
			}
			evt.stopPropagation();
		},

		_inlineKeyUp: function (evt) {

			evt.stopPropagation();

			if (evt.keyCode === ESCAPE || (evt.keyCode === F12 && evt.shiftKey)) { // esc
				this.$el.focus();
			}

		},

		_inlineInput: function (evt) {
			var $inlineEdit = this.$el.find('.inlineEdit');
			if ($(evt.target).val() === '' && $inlineEdit.hasClass('inputError')) {
				$inlineEdit.removeClass('inputError');
			}
		},

		_uiSetUrl: function () {
			var url = this.$el.find('.inlineEditInput').val();
			if (url !== this._lastSetURL) { //Suppress double executions (Enter setting the url, triggering a blur)
				this._lastSetURL = url;

				var errorData = {};
				var urlSetSuccessfully = this.setUrl(url, errorData);
				var $inlineEdit = this.$el.find('.inlineEdit');
				if (urlSetSuccessfully) {
					const $parentContainer = this._getParentContainer();
					if($parentContainer && $parentContainer.hasClass('urlWidgetInlineEdit')){
						$parentContainer.removeClass('urlWidgetInlineEdit');
					}
					$inlineEdit.removeClass('inputError');
				} else {
					$inlineEdit.addClass('inputError');
					$inlineEdit.find('.errorMessage').html(errorData.message);
					$inlineEdit.find('.emptyStateHelperLabel').hide();
				}
				this.refreshPropertiesPane();
			}
		},

		/**
		 * Virtual method
		 * @param The URL entered by the user
		 * @return true iff the URL is successfully set
		 */
		setUrl: function ( /*url, error data object*/ ) {
			return false;
		},

		/**
		 * Applies any mode-specific changes to the markup
		 * (e.g. authoring vs consume mode).
		 */
		updateModeMarkup: function () {
			if (this.isAuthoringMode) {
				this.onAuthoringMode();
			} else {
				this.onConsumeMode();
			}
			this.updateDescription(this.getLabel());
		},

		/**
		 * Updates markup, derived class may do more work
		 */
		updateMarkup: function () {
			this.updateModeMarkup();

		},

		onChromeSelected: function () {
			UrlWidget.inherited('onChromeSelected', this, arguments);
			this.onFocus();
			setTimeout(function () {
				$(this.el).find('.inlineEditInput').focus();
			}.bind(this), 10);
		},

		registerEventGroup: function () {
			// URL widgets are not added to event groups
		},

		encodeURL: function (URL) {
			//Run a decode/encode to block XSS attacks while not double-encoding already encoded URLs
			//As different parts of the URI encode different characters, we need to parse the URI to encode different parts using different functions.
			var URLComponents = URL.split('?');
			var urlPath = encodeURI(decodeURI(URLComponents[0])),
				searchPath = URLComponents[1];
			URLComponents = [];
			URLComponents.push(urlPath);
			if (searchPath) {
				var searchPathComponents = searchPath.split('&'),
					encodedSearchPathComponents = [];
				for (var i = 0; i < searchPathComponents.length; i++) {
					var paramComponents = searchPathComponents[i].split('='),
						encodedParamComponents = [];
					for (var j = 0; j < paramComponents.length; j++) {
						encodedParamComponents.push(encodeURIComponent(decodeURIComponent(paramComponents[j].replace(/\+/g, ' '))));
					}
					encodedSearchPathComponents.push(encodedParamComponents.join('='));
				}
				URLComponents.push(encodedSearchPathComponents.join('&'));
			}
			return URLComponents.join('?');
		},

		destroy: function() {
			$(this.el).off('click touchend', this._onClick.bind(this));
			UrlWidget.inherited('destroy', this, arguments);
		}
	});

	/**
	 * Checks whether the browser will render this URL as an embedded object
	 * (e.g. iframe, video, audio, embed, object, etc.)
	 */
	UrlWidget._isAllowedProtocol = function (url) {
		if (!url) {
			return false;
		}
		url = url.toLowerCase();

		// https:// is always allowed - i.e. both in pages served from HTTP or
		// 	HTTPS
		// // is always allowed as it will copy whichever protocol the
		// 	containing page is using
		// http:// is only allowed if the containing page is using HTTP as well
		if (url.indexOf('https://') === 0 || url.indexOf('//') === 0) {
			return true;
		}
		return false;
	};

	/**
	 * Checks whether the browser will render this URL as an embedded object
	 * (e.g. iframe, video, audio, embed, object, etc.)
	 */
	UrlWidget.isAllowedProtocol = function (url) {
		return UrlWidget._isAllowedProtocol(url);
	};

	UrlWidget.getDefaultConsumeMarkup = function (missingText) {
		return NewWidgetTemplate({
			missingUrlText: missingText
		});
	};

	return UrlWidget;
});
